import React, { useCallback, useEffect, useRef, useState } from "react";

import i18next, { t } from "i18next";
import { Trans } from "react-i18next";

import Clappr from "clappr";
import levelSelectorPlugin from "level-selector";
import ClapprStats from "clappr-stats";
import PlaybackRatePlugin from "clappr-playback-rate-plugin";
import { yoania } from "./yoania";

import {
  getHLSLiveOnDemandURL,
  getHLSOnDemandURL,
  getElementalVodUrl,
  getLiveEgressUrl,
} from "../../utils/urls";
import { getSecondsFromString, guid } from "../../utils/helper";

import { ENV, EVWEBSOCKET_URL, WEBSOCKET_URL } from "../../utils/settings";

import { metricsApi } from "../../api/metrics";

import { LiveCountdown } from "../liveCountdown";
import { Playlist } from "../playlist";
import { PlaylistRedirect } from "../playlistRedirect";
import { LivePausedIcon } from "../icons/LivePausedIcon";
import { LostConnectionIcon } from "../icons/LostConnectionIcon";
import { LiveFinishedIcon } from "../icons/LiveFinishedIcon";
import { IPlayer } from "../../interfaces/player";

import styles from "./Player.module.css";

const getMessage = (title: string, message: string, icon: React.ReactNode) => {
  return (
    <div className={styles.messagesContainer}>
      {icon}
      <span className={styles.title}>{title}</span>
      <span className={styles.subtitle}>{message}</span>
    </div>
  );
};

export function ClapprPlayer({
  talk,
  email,
  playerView,
  setPlayerView,
  playlist,
  channel,
  style,
}: IPlayer) {
  const playerRef = useRef(null);
  const [timeToStart, setTimeToStart] = useState<number>(talk.time_to_start);
  const [lastEventReceived, setLastEventReceived] = useState<string>();

  let retry = 0;
  let player: any = null;

  const createPlayer = (
    sources: string[],
    autostart: boolean,
    playbackType: string
  ) => {
    let metricsReceivedCount = 0;
    let attachedToHlsFragLoaded = false;

    const plugins = [levelSelectorPlugin, ClapprStats];

    if (playbackType === "vod") {
      plugins.push(PlaybackRatePlugin);
    }

    const clapprConfig = {
      width: "100%",
      height: "100%",
      language: i18next.language,
      sources: sources,
      poster: talk.cover,
      autoPlay: autostart,
      hideMediaControl: true,
      playbackNotSupportedMessage: (
        <Trans i18nKey="player.unsupported">
          O seu navegador não pode reproduzir este vídeo.
          <br />
          Verifique se você possui o{" "}
          <a
            href="https://get.adobe.com/flashplayer/"
            target="_blank"
            rel="noreferrer"
          >
            Adobe Flash Player
          </a>{" "}
          instalado e habilitado.
        </Trans>
      ),
      plugins: plugins,
      playback: {
        playInline: true,
        hlsjsConfig: {
          liveSyncDurationCount: 3,
          startLevel: 1,
          autoLevelEnabled: playbackType === "live",
          manifestLoadingTimeOut: 25000,
          levelLoadingTimeOut: 25000,
          nudgeOffset: 4,
          nudgeMaxRetry: 60,
          xhrSetup: function (xhr: any, url: string) {
            xhr.withCredentials = ENV === "production" ? true : false;
            var destiny = new URL(url);
            destiny.searchParams.set("playback", talk.playback_stream_name);
            destiny.searchParams.set("no-cached", new Date().toString());
            xhr.open("GET", destiny.toString(), true);
          },
        },
      },
      clapprStats: {
        // get and send metrics of video
        runEach: 5 * 1000,
        onReport: function (metrics: any) {
          metricsReceivedCount++;
          if (playbackType === "vod") {
            var durations = metrics.extra.watchHistory.map(function (
              hist: any
            ) {
              return Math.abs(hist[1] - hist[0]);
            });
            var duration = 0;
            durations.forEach(function (d: any) {
              duration += d;
            });
            metrics.timers.watch = duration;
          }
          if (metricsReceivedCount % 2 === 0) {
            // Quando o video ao vivo termina o wowza manda x-end-list,
            // nesse caso metrics.extra.playbackType do player é alterado para vod
            // gerando inconsistências nos logs de acesso.
            metricsApi.reportMetrics(metrics, talk, email);
          }
        },
      },
      clapprNerdStats: {
        shortcut: ["command+shift+s", "ctrl+shift+s"],
      },
      levelSelectorConfig: {
        title: t("quality"),
        labelCallback: function (playbackLevel: any) {
          return playbackLevel.level.height + "p";
        },
      },
      playbackRateConfig:
        playbackType === "vod"
          ? {
              defaultValue: 1.0,
              options: [
                { value: 0.5, label: "0.5x" },
                { value: 1.0, label: "1x" },
                { value: 1.5, label: "1.5x" },
                { value: 2.0, label: "2x" },
              ],
            }
          : {},
      events: {
        onError: function (event: any) {
          if (player) {
            player.stop();
          }
          // Display a error message if there's an error while loading or parsing the HLS manifest file
          if (
            event.raw.details === "manifestParsingError" ||
            event.raw.details === "manifestLoadError" ||
            event.raw.details === "manifestLoadTimeOut"
          ) {
            setPlayerView("error");
          } else {
            // Retry logic
            if (retry < 3) {
              retry++;
              setTimeout(() => {
                console.log("Retrying... Attempt: " + retry);
                player.configure({ sources: sources });
                setPlayerView(playerView);
                player.play();
              }, 5000); // Retry after 5 seconds
            } else {
              console.log("Retry limit reached. Stopping playback.");
              setPlayerView("error");
            }
          }
        },
        onReady: function (event: any) {
          const videoElement = document.querySelector<HTMLVideoElement>(
            "#evts-player video"
          ) as HTMLVideoElement;
          if (videoElement) {
            videoElement.playsInline = true;
            console.log(videoElement);
          }
        },
      },
    };

    const playerInstance = new Clappr.Player(clapprConfig);

    // if playlist redirect to the next webinars when this ended
    playerInstance.on(Clappr.Events.PLAYER_ENDED, function () {
      if (playlist) {
        const index = playlist.talks.indexOf(talk);
        if (index + 1 < playlist.talks.length) {
          setPlayerView("playlist_redirect");
          setTimeout(() => {
            window.location.href =
              window.location.host +
              "/" +
              channel +
              "/" +
              playlist.talks[index + 1].slug +
              "/?playlist=" +
              playlist.slug;
          }, 5000);
        }
      }
    });

    playerInstance.on(Clappr.Events.PLAYER_PLAY, function () {
      // Avoid duplicating event listeners.
      if (!attachedToHlsFragLoaded) {
        attachedToHlsFragLoaded = true;
        // The 'hlsFragLoaded' event is triggered by the HLS when an HLS fragment is loaded.
        player.core
          .getCurrentPlayback()
          ._hls.on("hlsFragLoaded", function (e: any, payload: any) {
            // When a fragment is loaded the callback hlsFragLoaded extract the necessary metrics.
            metricsApi.hlsFragLoaded(e, payload);
          });
      }
    });

    return playerInstance;
  };

  /**
   * load the Eventials websocket js and init the WsApi (Eventials websocket object)
   * The WsApi provide some webinars event like stream started or time to start live changed in real time
   */
  const initWebSocket = () => {
    const loadWebsocketScript = () => {
      return new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = EVWEBSOCKET_URL;
        script.type = "text/javascript";
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    loadWebsocketScript()
      .then(() => {
        const wsApiConfig = {
          url: WEBSOCKET_URL,
          debug: false,
          channel: talk.playback_stream_name,
          email: email,
          clientTalkId: guid(),
          is_moderator: false,
        };

        interface IWindow extends Window {
          WsApi?: any;
        }
        const currentWindow: IWindow = window;
        const wsApi = new currentWindow.WsApi["default"](
          wsApiConfig,
          WebSocket
        );

        // Events listener
        wsApi.iws.ws.addEventListener("open", () => {
          wsApi.onTalkStreamStarted(function () {
            // data return webinar state and live_playback_url
            setLastEventReceived("live");
            setTimeout(() => {
              setPlayerView("live");
            }, 5000);
          });

          wsApi.onTalkStartTimeChanged(function (data: any) {
            // data return playback_stream_name and time_to_start
            setLastEventReceived("startTimeChanged");
            setTimeToStart(data.time_to_start);
            setPlayerView("upcoming");
          });

          wsApi.onTalkVodPublished(function (data: any) {
            // data return webinar state (processing or recorded)
            setLastEventReceived(data.state);
            setTimeout(() => {
              setPlayerView(data.state);
            }, 5000);
          });

          wsApi.onClosedByServer(function (data: any) {
            console.log("disconnected", data);
            //setModalDisconnected(true);
          });
        });
      })
      .catch((error) => {
        console.error("Error loading WsApi script:", error);
      });
  };

  const setSlideSeek = (time: number) => {
    var positionInMillis = time * 1000;
    var state = yoania.bufferingControl.getState();

    if (state === yoania.BufferingState.READY) {
      yoania.mainTimelineControl.trigger("seek", [positionInMillis]);
    } else {
      yoania.bufferingControl.on("ready", function () {
        yoania.mainTimelineControl.trigger("seek", [positionInMillis]);
        yoania.bufferingControl.off("ready", () => {});
      });
    }
  };

  const setupAutoSeek = useCallback(
    (player: any) => {
      var adminSeekTo = talk.playback_offset;
      var urlSeekTo = getSecondsFromString(window.location.hash.substring(3));

      if (urlSeekTo > 0) {
        player.seek(urlSeekTo);
        if (ENV === "production") {
          setSlideSeek(urlSeekTo);
        }
      } else if (adminSeekTo > 0) {
        player.seek(adminSeekTo);
        if (ENV === "production") {
          setSlideSeek(adminSeekTo);
        }
      }
    },
    [talk.playback_offset]
  );

  const setupVodPlayer = useCallback(() => {
    let sources: string[] = [];

    if (talk.is_elemental) {
      sources.push(getElementalVodUrl(talk.playback_stream_name));
    } else {
      sources.push(getHLSOnDemandURL(talk.playback_stream_name));
    }

    const playerInstance = createPlayer(sources, true, "vod");

    // yoania controls the buffering state of playable content that requires synchronization
    yoania.bufferingControl.addResource(
      "video",
      yoania.BufferingState.BUFFERING
    );

    // notify when all streams have enough data to play
    yoania.bufferingControl.on("ready", function () {
      if (!playerInstance.isPlaying()) {
        playerInstance.play();
      }
    });

    // notify when at least one stream is buffering
    yoania.bufferingControl.on("buffering", function (resource: string) {
      if (resource !== "video") {
        playerInstance.pause();
        yoania.mainTimelineControl.trigger("pause", []);
      }
    });

    // pause the video if error on buffering
    yoania.bufferingControl.on("error", function (resource: string) {
      if (ENV === "production") {
        if (resource !== "video") {
          playerInstance.pause();
          yoania.mainTimelineControl.trigger("pause", []);
        }
      }
    });

    playerInstance.on(Clappr.Events.PLAYER_PAUSE, function () {
      yoania.mainTimelineControl.trigger("pause", []);
    });

    playerInstance.on(Clappr.Events.PLAYER_SEEK, function (time: number) {
      if (ENV === "production") {
        setSlideSeek(time);
      }
    });

    playerInstance.on(Clappr.Events.PLAYBACK_PLAY, function () {
      yoania.bufferingControl.changeResourceBufferingState(
        "video",
        yoania.BufferingState.READY
      );
    });

    playerInstance.on(Clappr.Events.PLAYBACK_BUFFERING, function () {
      yoania.bufferingControl.changeResourceBufferingState(
        "video",
        yoania.BufferingState.BUFFERING
      );
    });

    playerInstance.on(Clappr.Events.PLAYER_ENDED, function () {
      yoania.mainTimelineControl.trigger("complete", []);
      playerInstance.stop();
    });

    setupAutoSeek(playerInstance);

    return playerInstance;
  }, [talk, email, setupAutoSeek]);

  const setupLivePlayer = useCallback(async () => {
    try {
      let sources: string[] = [];

      const urls = [
        getHLSLiveOnDemandURL(talk.playback_stream_name),
        getLiveEgressUrl(talk.playback_stream_name),
      ];

      const requests = urls.map(async (url) => {
        try {
          const response = await fetch(url);

          if (response.ok) {
            return { url, status: response.status };
          } else {
            console.warn(
              `Request to ${url} failed with status ${response.status}`
            );
          }
        } catch (error) {
          console.error(`Error during request to ${url}:`, error);
        }
      });

      const results = await Promise.all(requests);

      const successfulResult = results.find(
        (result) => result && result.status === 200
      );

      if (successfulResult) {
        sources = [successfulResult.url];

        const playerInstance = createPlayer(sources, true, "live");

        playerInstance.on(Clappr.Events.PLAYER_ENDED, function () {
          playerInstance.stop();
          if (lastEventReceived && lastEventReceived === "processing") {
            setPlayerView("processing");
          } else if (lastEventReceived === "recorded") {
            setPlayerView("recorded");
          } else {
            setPlayerView("paused");
          }
        });

        return playerInstance;
      } else {
        console.error("No successful results");
      }
    } catch (error) {
      console.error("Error during requests:", error);
    }

    return null;
  }, [email, talk]);

  const clearRef = () => {
    if (playerRef.current) {
      (playerRef.current as HTMLElement).innerHTML = "";
    }
  };

  useEffect(() => {
    const setupPlayer = async () => {
      console.log("teste 2", player);

      if (playerView === "live" || talk.state === "live") {
        player = await setupLivePlayer();
      } else if (playerView === "recorded" || talk.state === "recorded") {
        player = await setupVodPlayer();
      }

      if (player) {
        clearRef();
        player.attachTo(playerRef.current);
      }
    };

    setupPlayer();

    return () => {
      if (player) {
        player.destroy();
        player = null;
      }
      clearRef();
    };
  }, [playerView]);

  useEffect(() => {
    initWebSocket();
  }, []);

  const getScreen = () => {
    switch (playerView) {
      case "upcoming":
        return <LiveCountdown cover={talk.cover} timeToStart={timeToStart} />;

      case "paused":
        return getMessage(
          t("player.live.paused.title"),
          t("player.live.paused.text"),
          <LivePausedIcon />
        );

      case "error":
        return getMessage(
          t("player.live.lostConnection.title"),
          t("player.live.lostConnection.text"),
          <LostConnectionIcon />
        );

      case "processing":
        const message = !talk.hide_recorded
          ? t("player.live.finished.recorded")
          : t("player.live.finished.hideRecorded");
        return getMessage(
          t("player.live.finished.title"),
          message,
          <LiveFinishedIcon />
        );

      case "playlist_redirect":
        if (playlist) {
          const index = playlist.talks.indexOf(talk);
          const nextTalk = playlist.talks[index + 1];
          return <PlaylistRedirect cover={nextTalk.cover} />;
        }

      default:
        return (
          <div className={styles.playerContainer}>
            <div
              className={styles.playerVideo}
              ref={playerRef}
              id="evts-player"
            />
          </div>
        );
    }
  };

  return (
    <div className={styles.container} style={style}>
      {getScreen()}
      {playlist && (
        <Playlist
          channel={channel}
          talks={playlist.talks}
          playlistName={playlist.name}
          playlistSlug={playlist.slug}
          currentTalk={talk}
        />
      )}
    </div>
  );
}
